import getVatRateFromProduct from "@/utils/elevate/getVatRateFromProduct";
import type { CustomProduct } from "@apptus/esales-api";

import getPriceInfoFromProduct from "@/utils/elevate/getPriceInfoFromProduct";
import { Market } from "@features/market/config/i18n-config";
import z from "zod";
import {
  getItemStatus,
  getLightsourceQuantity,
  getProductFlags,
  getStockOnHand,
  getVariantKey,
} from "./getProductFields";

export const cartProductSchema = z.object({
  key: z.string(),
  link: z.string(),
  name: z.string(),
  brand: z.string(),
  price: z.number(),
  partNo: z.string(),
  vatRate: z.number().optional(),
  imageKey: z.string(),
  quantity: z.number(),
  discount: z.number().optional(),
  itemStatus: z.string().optional(),
});

export type CartProduct = z.infer<typeof cartProductSchema>;

export type PurchaseProduct = CartProduct & {
  flags: string[];
  stockOnHand?: number;
  lightsourceQuantity: number;
};

export function toCartProduct(
  product: CustomProduct,
  market: Market,
  quantity: number = 1,
): CartProduct {
  const priceInfo = getPriceInfoFromProduct(product, market);
  return {
    key: product.key,
    partNo: getVariantKey(product),
    name: product.name ?? product.title,
    brand: product.brand,
    link: product.link,
    itemStatus: getItemStatus(product),
    imageKey: String(
      product.typedCustom?.json.v2_variantMainImageKey ??
        product.typedCustom?.json.v2_productMainImageKey ??
        "",
    ),
    price: priceInfo.price,
    discount: priceInfo.priceRrp - priceInfo.price,
    vatRate: getVatRateFromProduct(product, market),
    quantity,
  };
}

export function toPurchaseProduct(
  product: CustomProduct,
  market: Market,
  quantity: number = 1,
): PurchaseProduct {
  const onHand = getStockOnHand(product);

  return {
    ...toCartProduct(product, market, quantity),
    flags: getProductFlags(product),
    stockOnHand: Number.isFinite(onHand) ? onHand : undefined,
    lightsourceQuantity: getLightsourceQuantity(product),
  };
}
